import { Link, createFileRoute } from '@tanstack/react-router'
import {
    ArrowRight,
    Bell,
    FolderKanban,
    Network,
    ShieldAlert,
    ShieldOff,
    UserX,
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/data/EmptyState'
import { useMlroDashboard } from '@/hooks/api/useMlro'
import { useMlroClosures } from '@/hooks/api/useClosures'
import { useRoles } from '@/hooks/useRoles'
import { ApiError } from '@/lib/api'
import { formatTimestamp } from '@/lib/utils'

export const Route = createFileRoute('/mlro/')({
    component: MlroDashboardPage,
})

function MlroDashboardPage() {
    const { hasAnyRole } = useRoles()
    if (!hasAnyRole('mlro')) {
        return (
            <EmptyState
                icon={ShieldOff}
                title="No access"
                description="The MLRO dashboard is restricted to operators with the MLRO role."
            />
        )
    }

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">MLRO</h1>
                <p className="text-muted-foreground text-sm">
                    Triage alerts, run cases, and close them with notes. Counts refresh every 30
                    seconds.
                </p>
            </div>
            <DashboardBody />
        </div>
    )
}

function DashboardBody() {
    const query = useMlroDashboard()
    // The dashboard summary predates closures; the count comes straight off
    // the (MLRO-gated) closures list.
    const closuresQuery = useMlroClosures('pending_review')
    const pendingClosures = closuresQuery.data?.closures.length

    if (query.isLoading) {
        return (
            <div className="grid gap-4 md:grid-cols-3">
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
                <Skeleton className="h-32" />
            </div>
        )
    }

    if (query.isError) {
        const err = query.error
        const unauthorized = err instanceof ApiError && err.status === 401
        return (
            <EmptyState
                icon={ShieldAlert}
                title={unauthorized ? 'Session expired' : 'Could not load dashboard'}
                description={
                    unauthorized
                        ? 'Reload to re-authenticate.'
                        : err instanceof Error
                          ? err.message
                          : 'Unknown error'
                }
            />
        )
    }

    const data = query.data
    if (!data) return null

    return (
        <div className="space-y-3">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <PanelCard
                    title="Open alerts"
                    description="Unacknowledged alerts."
                    count={data.openAlerts}
                    to="/mlro/alerts"
                    search={{ acknowledged: 'false' }}
                    icon={Bell}
                    tone={data.openAlerts > 0 ? 'attention' : 'idle'}
                />
                <PanelCard
                    title="Open cases"
                    description="Cases in any state but closed."
                    count={data.openCases}
                    to="/mlro/cases"
                    search={{ status: 'open' }}
                    icon={FolderKanban}
                    tone={data.openCases > 0 ? 'attention' : 'idle'}
                />
                <PanelCard
                    title="Escalated cases"
                    description="Awaiting senior MLRO review."
                    count={data.escalatedCases}
                    to="/mlro/cases"
                    search={{ status: 'escalated' }}
                    icon={ShieldAlert}
                    tone={data.escalatedCases > 0 ? 'critical' : 'idle'}
                />
                <PanelCard
                    title="Account closures"
                    description="Pending net-€0 close-outs awaiting review."
                    count={pendingClosures}
                    to="/mlro/closures"
                    icon={UserX}
                    tone={(pendingClosures ?? 0) > 0 ? 'attention' : 'idle'}
                />
                <PanelCard
                    title="Sybil clusters"
                    description="Linked-wallet clusters in MLRO review."
                    to="/mlro/sybil"
                    search={{ status: 'review' }}
                    icon={Network}
                    tone="idle"
                />
            </div>
            <p className="text-muted-foreground text-[11px]">
                Refreshed {formatTimestamp(data.refreshedAt)}.
            </p>
        </div>
    )
}

interface PanelCardProps {
    title: string
    description: string
    count?: number
    to: string
    search?: Record<string, string>
    icon: React.ComponentType<{ className?: string }>
    tone: 'idle' | 'attention' | 'critical'
}

function PanelCard({ title, description, count, to, search, icon: Icon, tone }: PanelCardProps) {
    const accent =
        tone === 'critical'
            ? 'text-destructive'
            : tone === 'attention'
              ? 'text-in-progress'
              : 'text-muted-foreground'
    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between space-y-0">
                <div>
                    <CardTitle className="text-base">{title}</CardTitle>
                    <CardDescription>{description}</CardDescription>
                </div>
                <Icon className={`h-5 w-5 ${accent}`} />
            </CardHeader>
            <CardContent className="flex items-end justify-between">
                <span className={`text-3xl font-semibold ${accent}`}>{count ?? '—'}</span>
                <Link
                    to={to}
                    // Cast: TanStack Router types `search` as the destination
                    // route's validated shape; the link target validates here at
                    // runtime so we widen via `as any`.
                    search={search as never}
                    className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"
                >
                    View <ArrowRight className="h-3.5 w-3.5" />
                </Link>
            </CardContent>
        </Card>
    )
}
